import { useTranslate } from '@tolgee/react';
import { Dayjs } from 'lib/dayjs';
import { createContext, useContext, useMemo } from 'react';
import { StoreInfo } from 'services/api/dashboard';

export interface FilterStoreInfo {
  region?: General.UpperCaseRegion[];
  list?: StoreInfo[];
}

export interface DashboardFilterType {
  department?: Dashboard.Department;
  departments?: Dashboard.Department[];
  platform?: General.PlatformUpperCase | 'ALL';
  storeType?: Dashboard.ReceptionScore.StoreType;
  storeInfo: FilterStoreInfo;
  dateRange: [Dayjs, Dayjs];
  userIds?: number[];
}

export interface DashboardFilterCtxValue {
  filter: DashboardFilterType;
  platforms: General.PlatformUpperCaseTunnel[];
  hasTokopedia: boolean;
}

export const DashboardFilterCtx = createContext<DashboardFilterCtxValue | null>(null);
export const useDashboardFilter = () => {
  const val = useContext(DashboardFilterCtx);
  if (!val) {
    throw new Error('No DashboardFilterCtx value');
  }
  return val.filter;
};

export const useDashboardFilterContext = () => {
  const value = useContext(DashboardFilterCtx);
  if (!value) {
    throw new Error('No DashboardFilterCtx value');
  }
  return value;
};

export const useDashboardPlatformInfo = () => {
  const value = useContext(DashboardFilterCtx);

  if (!value) {
    throw new Error('No DashboardFilterCtx value');
  }

  const {
    platforms,
    filter: { platform },
  } = value;

  const platformInfo = useMemo(() => {
    const isAllPlatform = platform === 'ALL' || !platform;
    const only = (_platform: General.PlatformUpperCaseTunnel) =>
      platform === _platform ||
      (isAllPlatform && platforms.length === 1 && platforms[0] === _platform);
    const has = (_platform: General.PlatformUpperCaseTunnel) =>
      platform === _platform || (isAllPlatform && platforms.includes(_platform));

    const isOnlyMercado = only('MERCADO');
    const isOnlyTokopedia = only('TOKOPEDIA');
    const isHasMercado = has('MERCADO');
    const isHasTokopedia = has('TOKOPEDIA');
    const isOnlyOthers = !isHasMercado && !isHasTokopedia;
    const isMercadoAndOthers = isHasMercado && !isOnlyMercado && !isHasTokopedia;
    const isTokopediaAndOthers = isHasTokopedia && !isOnlyTokopedia && !isHasMercado;
    const isMercadoAndTokopedia =
      isHasMercado &&
      isHasTokopedia &&
      platforms.every((platform) => platform === 'MERCADO' || platform === 'TOKOPEDIA');
    const isAll = isHasMercado && isHasTokopedia && !isMercadoAndTokopedia;

    return {
      isOnlyMercado,
      isOnlyTokopedia,
      isOnlyMercadoOrOnlyTokopedia: isOnlyMercado || isOnlyTokopedia,
      isHasMercado,
      isHasTokopedia,
      isOnlyOthers,
      isMercadoAndOthers,
      isTokopediaAndOthers,
      isMercadoAndTokopedia,
      isAll,
    };
  }, [platform, platforms]);

  return { platformInfo };
};

export const useDashboardPlatformTips = () => {
  const { t } = useTranslate();

  const { platformInfo } = useDashboardPlatformInfo();

  return useMemo(() => {
    const tipsRender = (tipsMap: Partial<Record<keyof typeof platformInfo, string>>) => {
      for (const key of Object.keys(tipsMap)) {
        if (platformInfo[key]) {
          return tipsMap[key];
        }
      }
    };

    return {
      platformInfo,
      下单总量tips: tipsRender({
        isAll: t('下单总量tips', { x: 'Tokopedia、Mercado' }),
        isTokopediaAndOthers: t('下单总量tips', { x: 'Tokopedia' }),
        isMercadoAndOthers: t('下单总量tips', { x: 'Mercado' }),
      }),
      下单总金额tips: tipsRender({
        isAll: t('下单总金额tips', { x: 'Tokopedia、Mercado' }),
        isTokopediaAndOthers: t('下单总金额tips', { x: 'Tokopedia' }),
        isMercadoAndOthers: t('下单总金额tips', { x: 'Mercado' }),
      }),
      非COD下单量tips: tipsRender({
        isAll: t('非COD下单量tips', { x: 'Tokopedia、Mercado' }),
        isTokopediaAndOthers: t('非COD下单量tips', { x: 'Tokopedia' }),
        isMercadoAndOthers: t('非COD下单量tips', { x: 'Mercado' }),
      }),
      非COD下单总金额tips: tipsRender({
        isAll: t('非COD下单总金额tips', { x: 'Tokopedia、Mercado' }),
        isTokopediaAndOthers: t('非COD下单总金额tips', { x: 'Tokopedia' }),
        isMercadoAndOthers: t('非COD下单总金额tips', { x: 'Mercado' }),
      }),
      静默下单量tips: tipsRender({
        isAll: t('静默下单量tips'),
        isMercadoAndOthers: t('静默下单量tips'),
      }),
      静默下单总金额tips: tipsRender({
        isAll: t('静默下单总金额tips'),
        isMercadoAndOthers: t('静默下单总金额tips'),
      }),
      复购情况tips: tipsRender({
        isAll: t('复购情况tips1', { x: 'Tokopedia、Mercado' }),
        isTokopediaAndOthers: t('复购情况tips1', { x: 'Tokopedia' }),
        isMercadoAndOthers: t('复购情况tips1', { x: 'Mercado' }),
        isMercadoAndTokopedia: t('复购情况tips2'),
        isOnlyMercado: t('复购情况tips2'),
        isOnlyTokopedia: t('复购情况tips2'),
      }),
      复购订单总量tips: tipsRender({
        isAll: t('复购情况tips1', { x: 'Tokopedia、Mercado' }),
        isTokopediaAndOthers: t('复购情况tips1', { x: 'Tokopedia' }),
        isMercadoAndOthers: t('复购情况tips1', { x: 'Mercado' }),
        isMercadoAndTokopedia: t('复购情况tips2'),
        isOnlyMercado: t('复购情况tips2'),
        isOnlyTokopedia: t('复购情况tips2'),
      }),
      付款订单量tips: tipsRender({
        isOnlyMercado: t('付款订单量tips'),
      }),
      订单损耗量tips: tipsRender({
        isOnlyMercado: t('订单损耗量tips'),
      }),
      订单损耗率tips: tipsRender({
        isOnlyMercado: `${t('订单损耗量')}/${t('付款订单量')}`,
      }),
    };
  }, [platformInfo, t]);
};
